import type { PhoneNumber } from 'libphonenumber-js';
import { ParseError, parsePhoneNumberWithError } from 'libphonenumber-js';

import { schema } from '../schema';

/**
 * Modified version of the HTML5 (WHATWG) email regex.
 * * Unlike the standard spec, this version requires a Top-Level Domain (TLD)
 * by changing the final quantifier from '*' to '+'. This prevents
 * internal addresses like 'user@localhost' from passing.
 *
 * Note: We also require the final label to be at least two characters,
 * which rejects single-character TLDs if they ever become valid.
 */
const EMAIL_WITH_TLD_REGEX =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])$/;

const EDGE_WHITESPACE_AND_ZERO_WIDTH_REGEX =
    /^(?:\s|\u200B|\u200C|\u200D|\uFEFF)+|(?:\s|\u200B|\u200C|\u200D|\uFEFF)+$/g;

export function normalizeInputText(text: string): string {
    return text.replace(EDGE_WHITESPACE_AND_ZERO_WIDTH_REGEX, '');
}

export function getValidEmail(email: string): string | null {
    const normalized = normalizeInputText(email);

    return EMAIL_WITH_TLD_REGEX.test(normalized) ? normalized : null;
}

export function isEmailValid(email: string): boolean {
    return getValidEmail(email) !== null;
}

function isPossibleForExceptionalCountry(phoneNumber: PhoneNumber): boolean {
    const nonConformingCountries = schema.nonConformingCountries as readonly number[] | undefined;

    if (!nonConformingCountries?.length) {
        return false;
    }

    const countryCallingCode = parseInt(phoneNumber.countryCallingCode, 10);

    return nonConformingCountries.includes(countryCallingCode) && phoneNumber.isPossible();
}

export function tryParsePhoneNumber(phoneNumberString: string): PhoneNumber | null {
    if (!phoneNumberString) {
        return null;
    }
    try {
        // `extract: false` demands that the entire input string must be a phone number.
        return parsePhoneNumberWithError(phoneNumberString, { extract: false });
    } catch (error) {
        if (error instanceof ParseError) {
            return null;
        }

        throw error;
    }
}

export function isPhoneNumberValid(phoneNumberString: string): boolean {
    const phoneNumber = tryParsePhoneNumber(phoneNumberString);

    // If the string could not be parsed into a phone number object, it's not valid.
    if (!phoneNumber) {
        return false;
    }

    // A number is valid if it's strictly valid OR it passes our exception check for
    // non-conforming countries.
    return phoneNumber.isValid() || isPossibleForExceptionalCountry(phoneNumber);
}
